'use client'

import { useRef, useEffect } from 'react'

interface OtpInputProps {
  value: string
  onChange: (val: string) => void
  length?: number
  autoFocus?: boolean
}

const OtpInput = ({
  value,
  onChange,
  length = 6,
  autoFocus = true
}: OtpInputProps) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([])
  const sanitizedValue = value.replace(/\D/g, '').slice(0, length)
  const otp = (sanitizedValue + ' '.repeat(length)).slice(0, length).split('')

  useEffect(() => {
    if (autoFocus) {
      inputsRef.current[0]?.focus()
    }
  }, [autoFocus])

  const focusInput = (idx: number) => {
    inputsRef.current[idx]?.focus()
    inputsRef.current[idx]?.select()
  }

  const updateValue = (newValue: string) => {
    const clean = newValue.replace(/\D/g, '').slice(0, length)
    onChange(clean)
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    idx: number
  ) => {
    const input = e.target.value
    const char = input.replace(/\D/g, '')

    if (!char) return

    const newValue =
      sanitizedValue.slice(0, idx) + char + sanitizedValue.slice(idx + 1)

    updateValue(newValue)

    // Move forward.
    if (idx < length - 1) {
      focusInput(idx + 1)
    }
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) => {
    switch (e.key) {
      case 'Backspace': {
        e.preventDefault()

        if (sanitizedValue[idx]) {
          const newValue =
            sanitizedValue.slice(0, idx) + sanitizedValue.slice(idx + 1)
          updateValue(newValue)
        } else if (idx > 0) {
          // Move backwards.
          focusInput(idx - 1)

          const newValue =
            sanitizedValue.slice(0, idx - 1) + sanitizedValue.slice(idx)
          updateValue(newValue)
        }
        break
      }

      case 'ArrowLeft':
        e.preventDefault()
        if (idx > 0) focusInput(idx - 1)
        break

      case 'ArrowRight':
        e.preventDefault()
        if (idx < length - 1) focusInput(idx + 1)
        break

      case 'Enter':
        break
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()

    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, length)

    if (!pasted) return

    updateValue(pasted)

    const nextIndex = Math.min(pasted.length - 1, length - 1)
    focusInput(nextIndex)
  }

  return (
    <div
      className="d-flex gap-2 justify-content-center my-4"
      role="group"
      aria-label={`${length}-digit verification code input`}
    >
      {otp.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            inputsRef.current[i] = el
          }}
          type="tel"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digit.trim()}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onFocus={(e) => e.target.select()}
          onPaste={handlePaste}
          aria-label={`Digit ${i + 1} of ${length}`}
          className={`
            form-control text-center fw-bold fs-4 p-0 border-0 border-bottom rounded-0
            ${
              digit.trim()
                ? 'border-primary border-2 text-primary'
                : 'border-secondary border-1 text-secondary'
            }
            shadow-none
          `}
          style={{
            width: 48,
            height: 64,
            background: 'transparent',
            fontSize: '1.5rem'
          }}
        />
      ))}
    </div>
  )
}

export default OtpInput
